import {
  CAMPAIGN_STATUSES,
  OPPORTUNTY_TYPE,
  normalizeCampaignStatus,
  normalizeOpportunityType,
} from "./campaign-status.constants";

export interface OpportunityHierarchyItem {
  id: string;
  programId: string;
  name: string;
  description?: string;
  type?: OPPORTUNTY_TYPE;
  eventId?: string;
  surveyId?: string;
  neighborhoodId: string | null;
  neighborhoodName: string | null;
  startDate?: string;
  endDate?: string;
}

export interface ProgramHierarchyItem {
  id: string;
  campaignId: string;
  name: string;
  description?: string;
  status: CAMPAIGN_STATUSES;
  opportunityCount: number;
  opportunities: OpportunityHierarchyItem[];
}

export interface CampaignHierarchyItem {
  id: string;
  name: string;
  description?: string;
  status: CAMPAIGN_STATUSES;
  startDate?: string;
  endDate?: string;
  programCount: number;
  opportunityCount: number;
  programs: ProgramHierarchyItem[];
}

export interface CampaignHierarchyResponse {
  campaigns: CampaignHierarchyItem[];
}

/** Mirrors Backend's CampaignDashboardResponse. */
export interface CampaignDashboardResponse {
  summary: CampaignDashboardSummary;
  campaigns: CampaignDashboardItem[];
}

/** Mirrors Backend's CampaignDashboardSummaryResponse. */
export interface CampaignDashboardSummary {
  residentsEngaged: number;
  activeCampaignCount: number;
  communityPartnerCount: number;
  opportunitiesCompletedCount: number;
}

/** Mirrors Backend's CampaignDashboardItemResponse. */
export interface CampaignDashboardItem {
  campaignId: string;
  eventOpportunityCount: number;
  surveyOpportunityCount: number;
  opportunitiesWithoutNeighborhood: number;
  neighborhoodPopulation: number;
  surveyResponseCount: number;
  neighborhoods: CampaignNeighborhoodDashboardItem[];
}

/** Mirrors Backend's CampaignNeighborhoodDashboardResponse. */
export interface CampaignNeighborhoodDashboardItem {
  id: string;
  name: string;
  population: number;
  eventOpportunityCount: number;
  surveyOpportunityCount: number;
}

export interface CreateCampaignRequest {
  name: string;
  description?: string;
  status: CAMPAIGN_STATUSES;
  startDate?: string;
  endDate?: string;
}

export interface CreateCampaignResponse {
  id: string;
  name: string;
}

export interface CampaignImportResponse {
  campaignsCreated: number;
  programsCreated: number;
  opportunitiesCreated: number;
}

export interface CampaignImportValidationError {
  sheet: string;
  row?: number;
  field?: string;
  message: string;
}

export interface CreateProgramResponse {
  id: string;
  campaignId: string;
  name: string;
}

export interface ProgramListItem {
  id: string;
  campaignId: string;
  name: string;
  description?: string;
  status: string;
}

export interface ProgramListResponse {
  programs: ProgramListItem[];
}

export interface CreateProgramRequest {
  campaignId: string;
  name: string;
  description?: string;
  status: CAMPAIGN_STATUSES;
}

export interface UpdateProgramRequest {
  name: string;
  description?: string;
  status: CAMPAIGN_STATUSES;
}

export interface UpdateProgramResponse {
  id: string;
  campaignId: string;
  name: string;
  description?: string;
  status: CAMPAIGN_STATUSES;
}

export interface CreateOpportunityResponse {
  id: string;
  programId: string;
  name: string;
  type?: OPPORTUNTY_TYPE;
  eventId?: string;
  surveyId?: string;
  neighborhoodId: string | null;
  neighborhoodName: string | null;
  description?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateOpportunityResponse {
  id: string;
  programId: string;
  type?: OPPORTUNTY_TYPE;
  eventId?: string;
  surveyId?: string;
  neighborhoodId: string | null;
  neighborhoodName: string | null;
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}

export interface OpportunitySourcePayload {
  type: OPPORTUNTY_TYPE;
  eventId?: string;
  surveyId?: string;
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateOpportunityRequest {
  programId: string;
  type?: OPPORTUNTY_TYPE;
  eventId?: string;
  surveyId?: string;
}

export type UpdateOpportunityRequest = Omit<CreateOpportunityRequest, "programId">;

export function normalizeOpportunityPayload(
  payload: Omit<CreateOpportunityRequest, "programId">,
): Omit<CreateOpportunityRequest, "programId"> {
  const normalizedType = normalizeOpportunityType(payload.type);
  const basePayload = {
    ...payload,
    type: normalizedType,
  };

  if (normalizedType === OPPORTUNTY_TYPE.Event) {
    return {
      ...basePayload,
      eventId: payload.eventId ?? payload.surveyId,
      surveyId: undefined,
    };
  }

  return {
    ...basePayload,
    surveyId: payload.surveyId ?? payload.eventId,
    eventId: undefined,
  };
}

export function normalizeCampaignHierarchyResponse(
  response: CampaignHierarchyResponse,
): CampaignHierarchyResponse {
  return {
    campaigns: response.campaigns.map((campaign) => ({
      ...campaign,
      status: normalizeCampaignStatus(campaign.status),
      programs: campaign.programs.map((program) => ({
        ...program,
        status: normalizeCampaignStatus(program.status),
        opportunities: program.opportunities.map((opportunity) => ({
          ...opportunity,
          type: normalizeOpportunityType(opportunity.type),
        })),
      })),
    })),
  };
}

export function normalizeProgramListItem(program: ProgramListItem): ProgramListItem {
  return {
    ...program,
    status: normalizeCampaignStatus(program.status),
  };
}

export function normalizeCreateOpportunityPayload(
  payload: Omit<CreateOpportunityRequest, "programId">,
): Omit<CreateOpportunityRequest, "programId"> {
  return normalizeOpportunityPayload(payload);
}

export function getOpportunitySourceId(
  opportunity: Pick<OpportunityHierarchyItem, "type" | "eventId" | "surveyId" | "id">,
): string {
  if (opportunity.type === OPPORTUNTY_TYPE.Survey) {
    return opportunity.surveyId ?? opportunity.eventId ?? opportunity.id;
  }

  return opportunity.eventId ?? opportunity.surveyId ?? opportunity.id;
}
